import {emptyArray} from '@packages/utilities/fp/utils';
import {Pair} from '@packages/utilities/Pair';
import {
  EvalEnv,
  Component,
  Plugins,
  PluginsContext,
  EnvironmentContextType,
  PluginComponentContextType,
} from '@packages/systems/wfdl';
import {Qualified, QualifiedMap} from '@packages/systems/wfdl-Qualified';
import {SharedContextType} from '@packages/systems/core/records/SharedContext';
import {RenderTrackingContextItem} from '@packages/systems/element-tracking-types';

type ElementId = string;
type NativeIdPath = Array<string>;

/**
 * Each value in the array identifies a potential IX3 target. The NativeIdPath
 * is used in order to discriminate between multiple instances of the same
 * component when triggering interactions.
 */
export type Ix3Targets = Array<
  Pair<Pair<Component.ComponentName, ElementId>, NativeIdPath>
>;

export type Env = {
  topEvalEnv: EvalEnv.EvalEnvType;
  evalEnv: EvalEnv.EvalEnvType;
  componentContext: PluginComponentContextType;
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  sharedContext: SharedContextType;
  environmentContext: EnvironmentContextType;
  pluginsContext: PluginsContext;
  isComponentRootElement: boolean;
  ix3PotentialTargets: Ix3Targets;
  currentComponent: null | Qualified<Component.ComponentName>;
  seenComponents: QualifiedMap<Component.ComponentName, true>;
  nativeIdPath: NativeIdPath;
  trackingContext: Array<RenderTrackingContextItem>;
  /**
   * The Designer expects elements inside Dynamo RichText to have data
   * id attributes as if they were owned by the current component even
   * though they are not. This is to allow selecting instances inside.
   */
  __HACK__isDynamoRichText?: true;
};

/**
 * Reified subtrees (e.g. from CMS rich-text reification) are their own render
 * scope. The parent's accumulated walk-state shouldn't leak in.
 */
export const freshEnvForReifiedSubtree = (env: Env): Env => ({
  topEvalEnv: env.topEvalEnv,
  evalEnv: env.topEvalEnv,
  componentContext: env.componentContext,
  components: env.components,
  plugins: env.plugins,
  sharedContext: env.sharedContext,
  environmentContext: env.environmentContext,
  pluginsContext: env.pluginsContext,
  isComponentRootElement: false,
  ix3PotentialTargets: emptyArray,
  currentComponent: null,
  seenComponents: QualifiedMap.empty(),
  // Technically shouldn’t have a nativeIdPath since the element is coming
  // from outside.
  nativeIdPath: env.nativeIdPath,
  trackingContext: env.trackingContext,
});
